// Function index:
//   Archetype               — the six mechanical archetypes (mirrors the
//       server's server/src/characters/archetype.ts).
//   archetypeOf(className)  — pure ORDERED keyword map over the freeform
//       class text. CLIENT COPY for the live creation preview only; the
//       server is authoritative and re-derives independently. Order is
//       load-bearing — healer words before caster words, ranged before rogue.
//   ArchetypeInfo            — the per-archetype display record shape
//       ARCHETYPE_INFO holds (label, packageText, signature).
//   ARCHETYPE_INFO           — display data: label + a static package/affinity
//       summary + the signature move's label (or null for adventurer).
//   SKILL_NAMES               — the curated 14 skill names, in server order,
//       for the adventurer two-skill picker's dropdown options.
//
// Why: display-only duplication so typing a class name previews the
// mechanical archetype instantly, with no round trip. Data, not logic —
// nothing here is ever sent to the server or trusted; creation re-derives
// the archetype server-side from `class` alone.
//
// archetypes.test.ts keeps a VERBATIM manual copy of the server's
// server/src/characters/archetype.ts::ARCHETYPE_FIXTURES drift-pin table —
// edit BOTH together or the two suites drift apart silently.

export type Archetype = 'fighter' | 'ranger' | 'rogue' | 'mage' | 'adept' | 'adventurer';

// Ordered: first matching group wins. Word-boundary matching (the item
// catalogue's lesson: "cap" must not match "captain").
const KEYWORDS: ReadonlyArray<[Archetype, readonly string[]]> = [
  ['adept', ['priest', 'priestess', 'cleric', 'druid', 'shaman', 'healer', 'monk']],
  ['mage', ['mage', 'magus', 'wizard', 'sorcerer', 'sorceress', 'warlock', 'witch', 'sage']],
  ['ranger', ['ranger', 'hunter', 'archer', 'scout', 'bowman', 'marksman']],
  ['rogue', ['rogue', 'thief', 'assassin', 'burglar', 'cutpurse', 'spy', 'scoundrel']],
  ['fighter', ['fighter', 'knight', 'warrior', 'sellsword', 'barbarian', 'paladin', 'soldier', 'guard', 'mercenary', 'cavalier']],
];

export function archetypeOf(className: string | null): Archetype {
  const c = (className ?? '').toLowerCase();
  if (!c.trim()) return 'adventurer';
  for (const [archetype, words] of KEYWORDS) {
    if (words.some((w) => new RegExp(`\\b${w}\\b`).test(c))) return archetype;
  }
  return 'adventurer';
}

export interface ArchetypeInfo {
  label: string;
  // Static skill-package + weapon-affinity summary, e.g.
  // "survival 2, tracking 1 · bow affinity". Empty for adventurer (no
  // package — free skill picks instead).
  packageText: string;
  signature: string | null;
}

export const ARCHETYPE_INFO: Record<Archetype, ArchetypeInfo> = {
  fighter: { label: 'Fighter', packageText: 'athletics 2, intimidation 1 · blade/blunt affinity', signature: 'Power Strike' },
  ranger: { label: 'Ranger', packageText: 'survival 2, tracking 1 · bow affinity', signature: 'Aimed Shot' },
  rogue: { label: 'Rogue', packageText: 'stealth 2, streetwise 1 · pierce affinity', signature: 'Backstab' },
  mage: { label: 'Mage', packageText: 'arcana 2, lore 1', signature: 'Arcane Bolt' },
  adept: { label: 'Adept', packageText: 'medicine 2, insight 1 · blunt affinity', signature: 'Mend' },
  adventurer: { label: 'Adventurer', packageText: '', signature: null },
};

// Curated 14, in server order (server/src/characters/skill-list.ts::SKILL_NAMES).
export const SKILL_NAMES: readonly string[] = [
  'perception', 'survival', 'tracking', 'stealth', 'sleight of hand',
  'athletics', 'intimidation', 'persuasion', 'streetwise', 'insight',
  'lore', 'arcana', 'medicine', 'herbalism',
];
